import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { api } from '../api';
import { Clock, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const HistoryPage = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchHistory = async () => {
    try {
      const res = await api.get('/history/threads');
      setThreads(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/history/threads/${id}`);
      setThreads(prev => prev.filter(item => item.id !== id));
      toast.success("Conversation deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openThread = (id) => {
      navigate(`/chat?thread_id=${id}`);
  };

  return (
    <div className="h-full overflow-auto p-6 md:p-10 max-w-5xl mx-auto space-y-8 rounded-3xl lg-glass">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight text-white">My History</h1>
        <p className="text-white/50">Revisit past conversations.</p>
      </div>

      <Card className="lg-glass-strong border-white/10 text-white min-h-[500px] flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-cyan-400" />
              Past Threads
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 p-0 relative">
           <ScrollArea className="h-full max-h-[600px] p-4">
              {loading ? (
                  <div className="text-center py-10 text-white/30">Loading...</div>
              ) : threads.length === 0 ? (
                  <div className="text-center py-20 text-white/30 flex flex-col items-center">
                      <MessageSquare className="h-10 w-10 mb-4 opacity-50" />
                      <p>No conversations yet.</p>
                      <Button variant="link" className="text-cyan-400 mt-2" onClick={() => navigate('/chat')}>Start a new chat</Button>
                  </div>
              ) : (
                  <div className="grid grid-cols-1 gap-3">
                      {threads.map((thread) => (
                          <div 
                            key={thread.id} 
                            onClick={() => openThread(thread.id)}
                            className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all group cursor-pointer"
                          >
                              <div className="flex-1 overflow-hidden">
                                  <div className="flex items-center gap-3 mb-1">
                                      <p className="text-sm font-medium text-white/90 truncate">{thread.title}</p>
                                      <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[10px] h-5 capitalize">
                                          {thread.mode}
                                      </Badge>
                                  </div>
                                  <p className="text-xs text-white/40">
                                      Last active: {new Date(thread.updated_at).toLocaleString()}
                                  </p>
                              </div>
                              <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={(e) => handleDelete(e, thread.id)}
                                  className="text-white/20 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                  <Trash2 className="h-4 w-4" />
                              </Button>
                          </div>
                      ))}
                  </div>
              )}
           </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPage;
